import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface FaqItem {
  faqId: string;
  question: string;
  answer: string;
  categoryId: string | null;
  displayOrder: number;
}

interface UseFaqReturn {
  faqs: FaqItem[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
  refresh: () => void;
}

export function useFaq(categoryId?: string): UseFaqReturn {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: queryKeys.customerSupport.faqs(categoryId),
    queryFn: () => api.listSupportFaqs(categoryId),
    enabled: searchQuery === null,
  });

  const searchQueryResult = useQuery({
    queryKey: queryKeys.customerSupport.faqSearch(searchQuery ?? ''),
    queryFn: () => api.searchSupportFaqs(searchQuery!),
    enabled: searchQuery !== null && searchQuery.length > 0,
  });

  const activeQuery = searchQuery !== null ? searchQueryResult : listQuery;

  const search = useCallback((query: string) => {
    if (query.trim().length === 0) {
      setSearchQuery(null);
    } else {
      setSearchQuery(query);
    }
  }, []);

  const refresh = useCallback(() => {
    setSearchQuery(null);
    listQuery.refetch();
  }, [listQuery]);

  return {
    faqs: activeQuery.data?.faqs ?? [],
    loading: activeQuery.isLoading,
    error: activeQuery.error ? activeQuery.error.message : null,
    search,
    refresh,
  };
}
